import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Users, Euro } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Rooms = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <p className="text-[#8A9A86] text-xl">{t('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 
            className="text-5xl sm:text-6xl font-light tracking-tight text-[#2C302E] mb-4"
            style={{fontFamily: 'Cormorant Garamond, serif'}}
            data-testid="rooms-title"
          >
            {t('rooms.title')}
          </h1>
          <p 
            className="text-lg text-[#2C302E]/70"
            style={{fontFamily: 'Manrope, sans-serif'}}
          >
            {t('rooms.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-12">
          {rooms.map((room, idx) => (
            <motion.div
              key={room.room_id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <Card 
                className="overflow-hidden border border-[#E3E5DF]/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-all duration-500"
                data-testid={`room-card-${room.room_id}`}
              >
                <div className="relative h-72 overflow-hidden group">
                  <img
                    src={room.image_url}
                    alt={language === 'it' ? room.name_it : room.name_en}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>

                <div className="p-8">
                  <h3 
                    className="text-3xl font-light text-[#2C302E] mb-3 tracking-tight"
                    style={{fontFamily: 'Cormorant Garamond, serif'}}
                  >
                    {language === 'it' ? room.name_it : room.name_en}
                  </h3>
                  
                  <p 
                    className="text-[#2C302E]/70 mb-6 leading-relaxed"
                    style={{fontFamily: 'Manrope, sans-serif'}}
                  >
                    {language === 'it' ? room.description_it : room.description_en}
                  </p>

                  <div className="flex items-center gap-6 mb-6 text-sm text-[#2C302E]/60">
                    <div className="flex items-center gap-2">
                      <Users size={18} className="text-[#8A9A86]" />
                      <span>{room.max_guests} {t('rooms.guests')}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Euro size={18} className="text-[#8A9A86]" />
                      <span className="font-medium text-[#2C302E]">€{room.price} / {t('rooms.perNight')}</span>
                    </div>
                  </div>

                  {(language === 'it' ? room.amenities_it : room.amenities_en)?.length > 0 && (
                    <div className="mb-6">
                      <p className="text-sm font-medium text-[#2C302E]/60 mb-2">{t('rooms.amenities')}:</p>
                      <div className="flex flex-wrap gap-2">
                        {(language === 'it' ? room.amenities_it : room.amenities_en).map((amenity, i) => (
                          <span
                            key={i}
                            className="text-xs px-3 py-1 bg-[#F0F2EB] text-[#2C302E] rounded-full"
                          >
                            {amenity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <Button
                      data-testid={`view-details-button-${room.room_id}`}
                      onClick={() => navigate(`/rooms/${room.room_id}`)}
                      variant="outline"
                      className="flex-1 border-[#8A9A86] text-[#8A9A86] hover:bg-[#8A9A86] hover:text-white rounded-full py-6 transition-all"
                      style={{fontFamily: 'Manrope, sans-serif'}}
                    >
                      {language === 'it' ? 'Dettagli' : 'Details'}
                    </Button>
                    <Button
                      data-testid={`book-room-button-${room.room_id}`}
                      onClick={() => navigate(`/booking/${room.room_id}`)}
                      className="flex-1 bg-[#8A9A86] hover:bg-[#7A8A76] text-white rounded-full py-6 transition-transform hover:scale-[1.02]"
                      style={{fontFamily: 'Manrope, sans-serif'}}
                    >
                      {t('rooms.bookRoom')}
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};
